import { useEffect, useState } from "react";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, X, CalendarOff, Clock } from "lucide-react";

const WEEKDAYS = [
  ["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"],
  ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"],
];

const emptyDay = () => ({ start: "09:00", end: "18:00" });

export default function ClinicianAvailability() {
  const [avail, setAvail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newDayOff, setNewDayOff] = useState("");

  useEffect(() => {
    api.get("/clinician/availability/mine")
       .then((r) => setAvail(r.data))
       .catch(() => toast.error("Couldn't load availability"));
  }, []);

  if (!avail) return <DashLayout><div className="p-6 text-sm text-zinc-500">Loading…</div></DashLayout>;

  const dayWindows = (d) => avail.working_hours?.[d] || [];
  const setDay = (d, list) => setAvail({ ...avail, working_hours: { ...avail.working_hours, [d]: list } });
  const addWindow = (d) => setDay(d, [...dayWindows(d), emptyDay()]);
  const rmWindow  = (d, i) => setDay(d, dayWindows(d).filter((_, x) => x !== i));
  const updWindow = (d, i, patch) => setDay(d, dayWindows(d).map((w, x) => x === i ? { ...w, ...patch } : w));
  const addDayOff = () => {
    if (!newDayOff) return;
    setAvail({ ...avail, days_off: Array.from(new Set([...(avail.days_off || []), newDayOff])).sort() });
    setNewDayOff("");
  };
  const rmDayOff = (d) => setAvail({ ...avail, days_off: (avail.days_off || []).filter((x) => x !== d) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/clinician/availability/mine", {
        working_hours: avail.working_hours,
        days_off: avail.days_off,
        timezone: avail.timezone || "Europe/London",
      });
      setAvail(r.data);
      toast.success("Availability saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">Settings</div>
        <h1 className="fl-heading text-4xl mt-2">Your availability</h1>
        <p className="text-sm text-zinc-500 mt-2">Patients only see bookable slots within your working hours. Times shown in {avail.timezone || "Europe/London"}.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="fl-heading text-xl mb-4 flex items-center gap-2"><Clock size={16} className="text-[#f47a20]"/> Weekly working hours</div>
          <div className="space-y-3">
            {WEEKDAYS.map(([k, label]) => (
              <div key={k} className="fl-card p-4" data-testid={`avail-day-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="fl-heading text-base">{label}</div>
                  <button onClick={() => addWindow(k)} className="fl-btn-ghost text-xs" data-testid={`avail-add-${k}`}>
                    <Plus size={12}/> Add window
                  </button>
                </div>
                {dayWindows(k).length === 0 && (
                  <div className="text-xs text-zinc-500">Closed</div>
                )}
                <div className="space-y-2">
                  {dayWindows(k).map((w, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`avail-window-${k}-${i}`}>
                      <input type="time" value={w.start}
                             onChange={(e) => updWindow(k, i, { start: e.target.value })}
                             className="fl-input flex-1" data-testid={`avail-start-${k}-${i}`} />
                      <span className="text-xs text-zinc-500">to</span>
                      <input type="time" value={w.end}
                             onChange={(e) => updWindow(k, i, { end: e.target.value })}
                             className="fl-input flex-1" data-testid={`avail-end-${k}-${i}`} />
                      <button onClick={() => rmWindow(k, i)} className="text-zinc-400 hover:text-red-400 p-1"
                              aria-label={`Remove window ${i+1}`} data-testid={`avail-rm-${k}-${i}`}>
                        <X size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="fl-heading text-xl mb-4 flex items-center gap-2"><CalendarOff size={16} className="text-[#f47a20]"/> Days off</div>
          <div className="fl-card p-4">
            <div className="flex gap-2 mb-3">
              <input type="date" value={newDayOff}
                     onChange={(e) => setNewDayOff(e.target.value)}
                     className="fl-input flex-1" data-testid="avail-newdayoff" />
              <button onClick={addDayOff} disabled={!newDayOff} className="fl-btn text-xs" data-testid="avail-add-dayoff">
                <Plus size={12}/> Add
              </button>
            </div>
            {(avail.days_off || []).length === 0 && <div className="text-xs text-zinc-500">No days off scheduled.</div>}
            <div className="space-y-1">
              {(avail.days_off || []).map((d) => (
                <div key={d} className="flex items-center justify-between text-sm p-2 rounded-md hover:bg-white/5"
                     data-testid={`avail-dayoff-${d}`}>
                  <span>{new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
                  <button onClick={() => rmDayOff(d)} className="text-zinc-400 hover:text-red-400" data-testid={`avail-dayoff-rm-${d}`}>
                    <X size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <button onClick={save} disabled={saving} className="fl-btn" data-testid="avail-save">
          <Save size={14}/> {saving ? "Saving…" : "Save availability"}
        </button>
      </div>
    </DashLayout>
  );
}
